import React, { useState, useEffect, useMemo } from "react";
import { Tabs, Spin, Form, ConfigProvider } from "antd";
import {
  CalendarOutlined,
  SoundOutlined,
  TeamOutlined,
  BookOutlined,
  UserOutlined,
  ScheduleOutlined,
  SyncOutlined,
  CameraOutlined,
  AppleOutlined,
  ThunderboltOutlined,
  ExperimentOutlined,
  GlobalOutlined,
} from "@ant-design/icons";
import { fetchMusicGenres } from "../server/configuration/MusicGenresServer";
import { getActiveCountries } from "../server/configuration/FiltersServer";
import { searchProfilesMeili } from "../server/profiles/profilesServer";
import TrackFormModal from "../components/TrackFormModal";
import EventSyncTab from "./scraper/EventSyncTab";
import EnrichArtistTab from "./scraper/EnrichArtistTab";
import ITunesIdManager from "./scraper/ITunesIdManager";
import EventStoryGenerator from "./scraper/EventStoryGenerator";
import EnrichmentTab from "./scraper/EnrichmentTab";
import RaCitiesTab from "./scraper/RaCitiesTab";
import {
  EventScraper,
  MusicScraper,
  ArtistCatalog,
  StoryNews,
  ProfileScraper,
  EventCalendar,
} from "./scraper/index";
import {
  ListingPageHeader,
  listingAntTabsTheme,
  listingPanelClass,
} from "@/components/listing/ListingLayout";

const tabLabel = (IconCmp, text) => (
  <span className="inline-flex items-center gap-2 text-sm font-medium">
    <IconCmp className="text-base text-teal-600 dark:text-teal-400" />
    <span>{text}</span>
  </span>
);

const Scraper = () => {
  const [activeTab, setActiveTab] = useState("events");
  const [profiles, setProfiles] = useState([]);
  const [profilesLoading, setProfilesLoading] = useState(true);
  const [musicGenres, setMusicGenres] = useState([]);
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [countries, setCountries] = useState([]);

  const [eventData, setEventData] = useState(null);
  const [showEventForm, setShowEventForm] = useState(false);

  const [trackData, setTrackData] = useState(null);
  const [showTrackForm, setShowTrackForm] = useState(false);
  const [trackForm] = Form.useForm();

  const searchProfiles = async (query = "", additionalFilters = {}) => {
    try {
      const filters = {
        isActive: true,
        isVerified: true,
        limit: 50,
        offset: 0,
        ...additionalFilters,
      };

      const response = await searchProfilesMeili(query, filters);
      return response.data.filter((profile) => profile.profileType !== "ML");
    } catch (error) {
      console.error("Error searching profiles:", error);
      return [];
    }
  };

  useEffect(() => {
    const loadData = async () => {
      try {
        const [musicGenresResponse, initialProfiles, countriesResponse] =
          await Promise.all([
            fetchMusicGenres(),
            searchProfiles("", { limit: 20 }),
            getActiveCountries(),
          ]);

        if (initialProfiles) {
          setProfiles(initialProfiles);
        }

        if (musicGenresResponse && musicGenresResponse.data) {
          setMusicGenres(musicGenresResponse.data);
        }

        const countriesData =
          countriesResponse?.data?.data || countriesResponse?.data || [];
        setCountries(countriesData);
      } catch (error) {
        console.error("Error loading initial data:", error);
      } finally {
        setProfilesLoading(false);
      }
    };

    loadData();
  }, []);

  const handleTrackFormCancel = () => {
    setShowTrackForm(false);
    trackForm.resetFields();
    setTrackData(null);
  };

  const tabItems = useMemo(
    () => [
      {
        key: "full-enrichment",
        label: tabLabel(ThunderboltOutlined, "Full enrichment"),
        children: <EnrichmentTab />,
      },
      {
        key: "ra-cities",
        label: tabLabel(GlobalOutlined, "RA cities"),
        children: <RaCitiesTab />,
      },
      {
        key: "eventsync",
        label: tabLabel(SyncOutlined, "Event sync"),
        children: (
          <EventSyncTab
            countries={countries}
            searchProfiles={searchProfiles}
            musicGenres={musicGenres}
            profilesLoading={profilesLoading}
          />
        ),
      },
      {
        key: "enrich-artists",
        label: tabLabel(ExperimentOutlined, "Enrich artists"),
        children: <EnrichArtistTab countries={countries} />,
      },
      {
        key: "events",
        label: tabLabel(CalendarOutlined, "Event scraper"),
        children: (
          <EventScraper
            profiles={profiles}
            profilesLoading={profilesLoading}
            musicGenres={musicGenres}
            eventData={eventData}
            setEventData={setEventData}
            showEventForm={showEventForm}
            setShowEventForm={setShowEventForm}
            selectedProfile={selectedProfile}
            setSelectedProfile={setSelectedProfile}
            searchProfiles={searchProfiles}
          />
        ),
      },
      {
        key: "calendar",
        label: tabLabel(ScheduleOutlined, "Events calendar"),
        children: <EventCalendar />,
      },
      {
        key: "content-studio",
        label: tabLabel(CameraOutlined, "Content studio"),
        children: <EventStoryGenerator />,
      },
      {
        key: "music",
        label: tabLabel(SoundOutlined, "Music scraper"),
        children: (
          <MusicScraper
            musicGenres={musicGenres}
            selectedProfile={selectedProfile}
            setSelectedProfile={setSelectedProfile}
            showTrackForm={showTrackForm}
            setShowTrackForm={setShowTrackForm}
            trackForm={trackForm}
            setTrackData={setTrackData}
          />
        ),
      },
      {
        key: "catalog",
        label: tabLabel(TeamOutlined, "Artist catalog"),
        children: (
          <ArtistCatalog
            profiles={profiles}
            profilesLoading={profilesLoading}
            selectedProfile={selectedProfile}
            setSelectedProfile={setSelectedProfile}
            showTrackForm={showTrackForm}
            setShowTrackForm={setShowTrackForm}
            trackForm={trackForm}
            setTrackData={setTrackData}
            musicGenres={musicGenres}
            searchProfiles={searchProfiles}
          />
        ),
      },
      {
        key: "itunes-manager",
        label: tabLabel(AppleOutlined, "iTunes ID manager"),
        children: <ITunesIdManager />,
      },
      {
        key: "stories",
        label: tabLabel(BookOutlined, "Story news"),
        children: (
          <StoryNews
            profiles={profiles}
            profilesLoading={profilesLoading}
            searchProfiles={searchProfiles}
          />
        ),
      },
      {
        key: "profiles",
        label: tabLabel(UserOutlined, "Profile scraper"),
        children: <ProfileScraper musicGenres={musicGenres} />,
      },
    ],
    [
      countries,
      profiles,
      profilesLoading,
      musicGenres,
      eventData,
      showEventForm,
      selectedProfile,
      showTrackForm,
      trackForm,
    ]
  );

  if (profilesLoading) {
    return (
      <>
        <div
          className={`${listingPanelClass} flex min-h-[50vh] flex-col items-center justify-center gap-4 px-6 py-16`}
        >
          <Spin size="large" />
          <p className="text-center text-sm text-slate-600 dark:text-slate-400">
            Loading profiles, genres, and locations…
          </p>
        </div>
      </>
    );
  }

  return (
    <>
      <ListingPageHeader
        kicker="Operations"
        title="Content scraper"
        description="Import events, tracks, profiles, and stories from external sources. Pick a tool below—each tab keeps its own workflow."
      />

      <div
        className={`${listingPanelClass} overflow-hidden font-jakarta shadow-sm`}
      >
        <div className="border-b border-stone-100 bg-gradient-to-r from-teal-50/60 via-white to-stone-50/80 px-5 py-4 dark:border-slate-800 dark:from-teal-950/25 dark:via-slate-900 dark:to-slate-900/80">
          <div className="flex gap-3">
            <div
              className="mt-0.5 hidden h-9 w-1 shrink-0 rounded-full bg-teal-500 sm:block"
              aria-hidden
            />
            <div className="min-w-0">
              <p className="text-sm font-semibold text-slate-800 dark:text-slate-100">
                Before you scrape
              </p>
              <p className="mt-1 text-sm leading-relaxed text-slate-600 dark:text-slate-400">
                Respect platform terms and rate limits. Prefer test URLs first;
                published content still goes through your usual review flow.
              </p>
            </div>
          </div>
        </div>

        <ConfigProvider theme={listingAntTabsTheme}>
          <Tabs
            activeKey={activeTab}
            onChange={setActiveTab}
            type="line"
            size="middle"
            items={tabItems}
            tabBarGutter={4}
            className="scraper-tabs-v2 px-2 pt-2 sm:px-4 [&_.ant-tabs-nav]:mb-0 [&_.ant-tabs-nav::before]:border-stone-200 dark:[&_.ant-tabs-nav::before]:border-slate-700 [&_.ant-tabs-content-holder]:border-t [&_.ant-tabs-content-holder]:border-stone-100 [&_.ant-tabs-content-holder]:bg-stone-50/40 [&_.ant-tabs-content-holder]:px-3 [&_.ant-tabs-content-holder]:py-5 [&_.ant-tabs-content-holder]:sm:px-5 dark:[&_.ant-tabs-content-holder]:border-slate-800 dark:[&_.ant-tabs-content-holder]:bg-slate-950/40 [&_.ant-tabs-tab]:rounded-t-lg [&_.ant-tabs-tab]:transition-colors [&_.ant-tabs-tab:hover]:text-teal-700 dark:[&_.ant-tabs-tab:hover]:text-teal-300"
            destroyInactiveTabPane={false}
          />
        </ConfigProvider>
      </div>

      <TrackFormModal
        visible={showTrackForm}
        onCancel={handleTrackFormCancel}
        form={trackForm}
        musicGenres={musicGenres}
        selectedProfile={selectedProfile}
        profiles={profiles}
        profilesLoading={profilesLoading}
        searchProfiles={searchProfiles}
      />
    </>
  );
};

export default Scraper;
